import {
  BucketPerformanceSeries,
  BucketPerformanceSeriesSet,
  BucketTotals,
} from "helpers/types";
import { BucketTwrFormula } from "constants/index";
import { BUCKET_KEYS, BucketKey } from "components/buckets/BucketToggle";
import {
  bucketSeriesColors,
  memberSeriesColors,
  seriesDashPatterns,
  stocksSeriesDash,
} from "styles/ChartsReport.styles";

/**
 * The whole-bucket option, named once.
 *
 * It is the drop-down's sentinel value, and lives here beside ONLY_STOCKS
 * rather than in BucketSeriesSelect, which already imports this module.
 */
export const ALL_SECURITIES = "All Securities";

/**
 * The stock-only option, named once: it is BOTH the drop-down's second
 * sentinel value and half of the line's legend title, so it lives here rather
 * than in BucketSeriesSelect, which already imports this module — the other
 * direction would be a cycle.
 *
 * Bucket 1 alone offers it, and the RUN decides that rather than this
 * module — the row is offered exactly when the bucket's series set carries a
 * `stocks` series (hasStocksSeries), so a bucket that could not be given one
 * never shows a control nothing draws.
 */
export const ONLY_STOCKS = "Only Stocks";

/**
 * The chart selection for ONE bucket. `all` and `symbols` are independent
 * rather than a single list with an "all" sentinel in it, because they are
 * independent to the reader: All Securities and any number of the bucket's
 * own securities can be picked together, and each draws its own line. A
 * single list would also have to carry a value no symbol can collide with,
 * which is exactly the sentinel the drop-down only needs at its own edge.
 */
export interface BucketSelection {
  all: boolean;
  /**
   * The bucket's stock-only line, independent of `all` and of `symbols` for
   * the reason those two are independent of each other: it is a THIRD thing
   * the reader can put on the chart beside them, not a mode either of them
   * switches into. Only Bucket 1 ever offers it, so it is `false` on the
   * other two and nothing reads it there.
   */
  stocks: boolean;
  symbols: string[];
}

export type ChartSelections = Record<BucketKey, BucketSelection>;

export const EMPTY_SELECTION: BucketSelection = {
  all: false,
  stocks: false,
  symbols: [],
};

/**
 * All Securities on every bucket — what the section OPENS on.
 *
 * Held for all three regardless of what the run turned out to carry, because
 * the selection is made before there is a run to consult: this is the state
 * the component mounts with, and `bucketTotals` is null until Calculate has
 * finished. So the default is never pruned and never re-applied when a run
 * arrives: a bucket this run does not carry contributes nothing anyway
 * (buildChartSpecs skips it), and the selection it is still holding is what
 * puts it back on the chart when the NEXT run does carry it.
 *
 * What that costs is a DISPLAY problem rather than a chart one, and it is
 * ChartsReport that settles it: a drop-down disabled for want of data must
 * not read "All Securities", which claims a selection nothing draws — so the
 * disabled control is shown EMPTY_SELECTION while the real one stays held.
 */
export const ALL_SELECTIONS: ChartSelections = {
  // Only Stocks opens OFF, on Bucket 1 included. The section opens on the
  // three bucket lines and narrows from there, and a second Bucket 1 line
  // nobody asked for would be read as part of that default rather than as
  // the answer to a question — which is what it is.
  "1": { all: true, stocks: false, symbols: [] },
  "2": { all: true, stocks: false, symbols: [] },
  "3": { all: true, stocks: false, symbols: [] },
};

/**
 * One LINE on the overlaid chart: its legend title, the series it draws, whose
 * bucket it is, and how it is stroked.
 *
 * The stroke is resolved HERE rather than at render time because it has to be
 * stable against the SELECTION, not against the drawn order: a reader
 * unticking one security must not repaint the lines that remain. So a
 * security's colour comes from its position among every security the RUN
 * carries, which does not move when the selection does.
 */
export interface ChartSpec {
  key: string;
  bucketKey: BucketKey;
  title: string;
  series: BucketPerformanceSeries;
  /** The line's colour. */
  color: string;
  /** Its SVG `stroke-dasharray`; "" for a solid line. */
  dash: string;
}

/**
 * Every security the run carries, keyed `<bucket>:<symbol>`, numbered in
 * bucket then symbol order.
 *
 * Built from the whole run and not from the selection — that is the entire
 * point (see ChartSpec). Numbering across buckets rather than within each one
 * is what stops Bucket 1's first security and Bucket 2's first security
 * arriving on screen in the same colour.
 */
const memberSlots = (
  bucketTotals: BucketTotals | null | undefined,
): Map<string, number> => {
  const slots = new Map<string, number>();
  let next = 0;
  for (const bucketKey of BUCKET_KEYS) {
    for (const symbol of bucketSecurities(bucketTotals, bucketKey)) {
      slots.set(`${bucketKey}:${symbol}`, next);
      next += 1;
    }
  }
  return slots;
};

const seriesSet = (
  bucketTotals: BucketTotals | null | undefined,
  bucketKey: BucketKey,
): BucketPerformanceSeriesSet | undefined =>
  bucketTotals?.[bucketKey]?.performance_series;

/**
 * The securities a bucket's drop-down offers, sorted by symbol.
 *
 * Sorted here rather than trusted from the response: the order of a JSON
 * object's keys is its insertion order, which is the backend's job ordering
 * and not a promise. Symbol order is also what the report's own member
 * ordering uses, so the list matches the rest of the page.
 *
 * Bucket 3 has no members by design and so offers none.
 */
export const bucketSecurities = (
  bucketTotals: BucketTotals | null | undefined,
  bucketKey: BucketKey,
): string[] => {
  const set = seriesSet(bucketTotals, bucketKey);
  return set ? Object.keys(set.members).sort() : [];
};

/** Whether a bucket took part in this run at all. */
export const hasBucketData = (
  bucketTotals: BucketTotals | null | undefined,
  bucketKey: BucketKey,
): boolean => seriesSet(bucketTotals, bucketKey) !== undefined;

/**
 * Whether this bucket's drop-down offers the Only Stocks row — which is to
 * say whether the run produced a stock-only series for it at all.
 *
 * The RUN decides, never this module and never the bucket number: the
 * backend gives the series to Bucket 1 alone, so reading the payload keeps
 * the two ends from disagreeing about which bucket has one, and a response
 * cached before the field shipped is simply a run that offers no row.
 *
 * A series carrying a REFUSAL instead of points still counts as offered: the
 * chart shows that sentence in place of the line, which is what every other
 * series on it does, and hiding the control instead would leave the reader
 * unable to ask the question the answer explains.
 */
export const hasStocksSeries = (
  bucketTotals: BucketTotals | null | undefined,
  bucketKey: BucketKey,
): boolean => seriesSet(bucketTotals, bucketKey)?.stocks !== undefined;

/**
 * The securities a selection names that the CURRENT run actually carries.
 *
 * A selection outlives the run it was made against — the reader changes the
 * period or the account and calculates again — and the new run's buckets
 * need not hold the same members. Pruning at read time keeps a stale symbol
 * out of both the drop-down's value and the charts without an effect that
 * rewrites the selection behind the reader's back, which would silently
 * discard a security that the NEXT run brings back.
 */
export const selectedSecurities = (
  bucketTotals: BucketTotals | null | undefined,
  bucketKey: BucketKey,
  selection: BucketSelection,
): string[] => {
  const available = bucketSecurities(bucketTotals, bucketKey);
  return available.filter((symbol) => selection.symbols.includes(symbol));
};

/**
 * A security's line key, which has to be safe to use as a CSS class suffix.
 *
 * The key reaches the DOM as x-charts' own `MuiLineElement-series-<id>` class,
 * and the chart hangs the line's dash pattern on exactly that class through
 * `sx` — where emotion emits the selector VERBATIM, escaping nothing. A symbol
 * is a ticker in practice, but the column it arrives from is a plain
 * 100-character CharField with no validator, so a `{` or `;` in one would
 * close the selector and write rules of its own. Hence a replace rather than
 * trust.
 *
 * The SLOT is part of the key because replacing characters can map two
 * distinct symbols onto one string, and two lines sharing an id collapse into
 * one series. It is drawn from the whole run rather than the selection, so it
 * is as stable as the colour beside it (see ChartSpec).
 *
 * The TITLE keeps the real symbol — it is rendered as escaped text, never as a
 * selector.
 */
const memberLineKey = (
  bucketKey: BucketKey,
  symbol: string,
  slot: number,
): string =>
  `bucket-${bucketKey}-${slot}-${symbol.replace(/[^A-Za-z0-9_-]/g, "_")}`;

/**
 * The symbols this selection draws from MORE THAN ONE bucket of this run —
 * the only lines whose bare symbol would not tell them apart (see
 * buildChartSpecs).
 */
const clashingSymbols = (
  bucketTotals: BucketTotals | null | undefined,
  selections: ChartSelections,
): Set<string> => {
  const seen = new Set<string>();
  const clashing = new Set<string>();
  for (const bucketKey of BUCKET_KEYS) {
    if (!seriesSet(bucketTotals, bucketKey)) continue;
    for (const symbol of selectedSecurities(
      bucketTotals,
      bucketKey,
      selections[bucketKey],
    )) {
      if (seen.has(symbol)) clashing.add(symbol);
      seen.add(symbol);
    }
  }
  return clashing;
};

/**
 * One LINE per selection, in drop-down order: each bucket's All Securities
 * line first, then its Only Stocks line, then its individually picked
 * securities. They are drawn
 * OVERLAID on one set of axes, so this is the order of the legend and of the
 * tooltip's rows rather than an order of charts down the page.
 *
 * All Securities and the securities under it are drawn TOGETHER when both are
 * picked. The bucket's own line is a weighted return over the whole bucket
 * and not the sum of the lines beside it, so the two are named apart rather
 * than kept apart — "Bucket 2" against "TSLA" — and the bucket line keeps its
 * own accent and a solid stroke, which no security is given.
 *
 * The title is what the reader identifies a line by in the legend, the
 * tooltip and the PDF, and it is kept as short as that allows (operator, task
 * 5124): the bucket's own line is the bare bucket name and a security is its
 * bare symbol. A symbol drawn from TWO buckets at once would read identically
 * twice, so only then is each of those lines given its bucket —
 * "META (Bucket 1)" beside "META (Bucket 2)". Only Stocks keeps its full
 * "Bucket 1: Only Stocks" title. A bucket absent from this run contributes
 * nothing, however it is selected.
 *
 * `formula` is the Summary gear's pick and moves ONLY the bucket's own line
 * (operator, task 3764) -- the securities under it are the same points under
 * either formula, because the formula weights the bucket and not the
 * securities in it. Switching it re-draws from the payload already on
 * screen, which is what keeps the pick free of a re-run.
 *
 * It DEFAULTS to the report's own default formula, which is what a payload
 * carrying no per-formula lines resolves to anyway: `bucket_by_formula` is
 * absent on a job cached before they shipped, so the read below falls back
 * to `bucket` and such a caller draws exactly what it drew before. Every
 * caller with a gear to read passes the pick.
 */
export const buildChartSpecs = (
  bucketTotals: BucketTotals | null | undefined,
  selections: ChartSelections,
  formula: BucketTwrFormula = BucketTwrFormula.DAILY_WEIGHT_ADJUSTMENT,
): ChartSpec[] => {
  const specs: ChartSpec[] = [];
  const slots = memberSlots(bucketTotals);
  const clashing = clashingSymbols(bucketTotals, selections);
  for (const bucketKey of BUCKET_KEYS) {
    const set = seriesSet(bucketTotals, bucketKey);
    if (!set) continue;
    const selection = selections[bucketKey];

    if (selection.all) {
      specs.push({
        key: `bucket-${bucketKey}`,
        bucketKey,
        // The bare bucket name: beside it a security reads as its bare
        // symbol, so the two cannot be mistaken for each other.
        title: `Bucket ${bucketKey}`,
        // THE PICKED FORMULA'S line, falling back to the run's own
        // (operator, task 3764). The fallback covers a job cached before
        // the per-formula lines shipped, which carries `bucket` alone.
        series: set.bucket_by_formula?.[formula] ?? set.bucket,
        // A bucket's own line keeps the accent its drop-down has always worn,
        // so the section's default view — the three buckets and nothing else —
        // reads exactly as it did when each had a chart to itself. It is also
        // what tells the aggregate apart from the securities drawn with it:
        // no security is ever given a bucket accent (see memberSeriesColors).
        color: bucketSeriesColors[bucketKey],
        dash: "",
      });
    }
    // Drawn ONLY when the run gave this bucket a stock-only series, so a
    // selection still holding `stocks` from a run that had one cannot put a
    // line on a chart this run has no data for — the same read-time pruning
    // selectedSecurities performs on a stale symbol, and for the same reason:
    // the held selection is what puts the line back when the next run carries
    // one again.
    if (selection.stocks && set.stocks) {
      specs.push({
        key: `bucket-${bucketKey}-stocks`,
        bucketKey,
        title: `Bucket ${bucketKey}: ${ONLY_STOCKS}`,
        series: set.stocks,
        // The bucket's OWN accent, because this is a second whole-bucket
        // aggregate rather than a security in it — the rule that keeps a
        // bucket accent off every member line (see memberSeriesColors) is
        // about telling an aggregate from a member, and this is an
        // aggregate. What tells it from the bucket's other line is the
        // DASH: the two are the same bucket measured two ways, so they are
        // deliberately the same colour and never the same stroke.
        color: bucketSeriesColors[bucketKey],
        dash: stocksSeriesDash,
      });
    }
    for (const symbol of selectedSecurities(
      bucketTotals,
      bucketKey,
      selection,
    )) {
      const slot = slots.get(`${bucketKey}:${symbol}`) ?? 0;
      specs.push({
        key: memberLineKey(bucketKey, symbol, slot),
        bucketKey,
        title: clashing.has(symbol)
          ? `${symbol} (Bucket ${bucketKey})`
          : symbol,
        series: set.members[symbol],
        color: memberSeriesColors[slot % memberSeriesColors.length],
        dash:
          seriesDashPatterns[
            Math.floor(slot / memberSeriesColors.length) %
              seriesDashPatterns.length
          ],
      });
    }
  }
  return specs;
};
